import { captureChatException } from 'shared/sentry';
import {
  createAbortError,
  createTimeoutController,
  createTimeoutError,
  isTimeoutError,
  withTimeout,
} from 'shared/services/call/callMediaAsync';
import {
  acquireCallVideoTrack,
  adoptLiveVideoTrackIntoCallCaptureCache,
  releaseUnadoptedCallVideoTrack,
} from 'shared/services/call/callMediaCaptureService';
import {
  getVideoDeviceIdFromStream,
  getVideoFacingModeFromStream,
  replaceVideoTrackOnStream,
  streamHasLiveVideo,
} from 'shared/services/call/callMediaTrackUtils';
import { isAbortError } from 'shared/utils/isAbortError';

const SDK_MEDIA_CONTROL_TIMEOUT_MS = 1800;
const SDK_REPLACE_TRACK_TIMEOUT_MS = 2500;
// Camera ON may need getUserMedia (after the SDK's 120ms stop-timer removes the track).
// getUserMedia can take 2-5 seconds on iOS Safari / slow devices re-acquiring the camera,
// so we use a much longer timeout to avoid firing before the acquisition completes and
// triggering concurrent SDK calls that leave the camera in a broken state.
const SDK_CAMERA_ON_TIMEOUT_MS = 8000;
// matrix-js-sdk transceiver map keys: `${SDPStreamMetadataPurpose}:${kind}`
const MATRIX_USERMEDIA_VIDEO_TRANSCEIVER_KEY = 'm.usermedia:video';
const MATRIX_SCREENSHARE_VIDEO_TRANSCEIVER_KEY = 'm.screenshare:video';

const isCallEnded = (call) => !call || Boolean(call.callHasEnded?.());

const getCurrentVideoDeviceId = (localStream) => getVideoDeviceIdFromStream(localStream);

const FRONT_CAMERA_RE = /front|user|facetime|selfie|facing.?user/i;
const BACK_CAMERA_RE = /back|rear|environment|world|facing.?back|facing.?environment/i;

const classifyVideoDeviceFacing = (device) => {
  const label = device?.label || '';
  if (BACK_CAMERA_RE.test(label)) return 'environment';
  if (FRONT_CAMERA_RE.test(label)) return 'user';
  return null;
};

const pickCallCameraForFacing = (videoInputs, facingMode) => {
  const match = videoInputs.find((device) => classifyVideoDeviceFacing(device) === facingMode);
  if (match) return match;
  if (facingMode === 'user') return videoInputs[0] ?? null;
  if (facingMode === 'environment' && videoInputs.length > 1) {
    return videoInputs[1] ?? null;
  }
  return null;
};

const pickFlipCameraTarget = (videoInputs, currentId, currentFacing) => {
  const currentDevice = videoInputs.find((device) => device.deviceId === currentId) ?? null;
  const facing = currentFacing || classifyVideoDeviceFacing(currentDevice) || 'user';
  const nextFacing = facing === 'environment' ? 'user' : 'environment';
  const nextDevice = pickCallCameraForFacing(videoInputs, nextFacing);
  if (nextDevice?.deviceId && nextDevice.deviceId !== currentId) {
    return { deviceId: nextDevice.deviceId, facingMode: nextFacing };
  }
  const fallback = videoInputs.find((device) => device.deviceId !== currentId);
  if (!fallback?.deviceId) return null;
  return {
    deviceId: fallback.deviceId,
    facingMode: classifyVideoDeviceFacing(fallback) || nextFacing,
  };
};

const syncMediaHandlerVideoInput = (client, deviceId) => {
  if (!client || !deviceId) return;
  const mh = client.getMediaHandler?.();
  if (!mh) return;
  try {
    if (typeof mh.restoreMediaSettings === 'function') {
      mh.restoreMediaSettings(mh.audioInput, deviceId);
    } else {
      mh.videoInput = deviceId;
    }
  } catch (_) {}
};

const enumerateVideoInputDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      ok: true,
      videoInputs: devices.filter((d) => d.kind === 'videoinput' && d.deviceId),
    };
  } catch {
    return { ok: false, reason: 'enumerate' };
  }
};

/**
 * Ensures local mic tracks match the desired send state when the SDK path fails
 * (e.g. metadata send throws after tracks were already updated) or is unavailable.
 */
const collectCallLocalMediaStreams = (call) => {
  const streams = new Set();
  if (!call) return streams;
  try {
    const s = call.localUsermediaStream;
    if (s) streams.add(s);
  } catch (_) {}
  try {
    (call.getLocalFeeds?.() ?? []).forEach((feed) => {
      if (feed?.stream) streams.add(feed.stream);
    });
  } catch (_) {}
  return streams;
};

export const setLocalOutboundAudioTracksEnabled = (call, enabled) => {
  collectCallLocalMediaStreams(call).forEach((stream) => {
    stream.getAudioTracks?.().forEach((track) => {
      track.enabled = enabled;
    });
  });
};

/**
 * Ensures local camera tracks match the desired send state when the SDK path fails
 * or is unavailable (mirrors {@link setLocalOutboundAudioTracksEnabled} for video).
 */
export const setLocalOutboundVideoTracksEnabled = (call, enabled) => {
  collectCallLocalMediaStreams(call).forEach((stream) => {
    stream.getVideoTracks?.().forEach((track) => {
      track.enabled = enabled;
    });
  });
};

const callHasLiveVideoTrack = (call) =>
  [...collectCallLocalMediaStreams(call)].some((stream) => streamHasLiveVideo(stream));

const collectLiveLocalVideoTracks = (call) => {
  const tracks = [];
  const seen = new Set();
  collectCallLocalMediaStreams(call).forEach((stream) => {
    (stream.getVideoTracks?.() ?? []).forEach((track) => {
      if (!track || track.readyState !== 'live' || seen.has(track.id)) return;
      seen.add(track.id);
      tracks.push(track);
    });
  });
  return tracks;
};

/** True when nothing live is sending, or every live local video track is disabled. */
const getLocalCameraOffState = (call) => {
  const liveVideoTracks = collectLiveLocalVideoTracks(call);
  if (!liveVideoTracks.length) return true;
  return liveVideoTracks.every((track) => track.enabled === false);
};

const transceiverCanSend = (transceiver) => {
  const direction = transceiver?.direction;
  const current = transceiver?.currentDirection;
  return (
    direction === 'sendrecv' ||
    direction === 'sendonly' ||
    current === 'sendrecv' ||
    current === 'sendonly'
  );
};

const getCallVideoSender = (call) => {
  const usermediaSender = call?.transceivers?.get?.(MATRIX_USERMEDIA_VIDEO_TRANSCEIVER_KEY)?.sender;
  if (usermediaSender) return usermediaSender;

  const pc = call?.peerConn;
  if (!pc) return null;

  const screenshareSender =
    call?.transceivers?.get?.(MATRIX_SCREENSHARE_VIDEO_TRANSCEIVER_KEY)?.sender ?? null;
  const localVideoIds = new Set(
    (call.localUsermediaStream?.getVideoTracks?.() ?? [])
      .map((track) => track?.id)
      .filter(Boolean)
  );

  const byLocalTrack = (pc.getSenders?.() ?? []).find(
    (sender) =>
      sender &&
      sender !== screenshareSender &&
      sender.track?.kind === 'video' &&
      localVideoIds.has(sender.track.id)
  );
  if (byLocalTrack) return byLocalTrack;

  // After mute, sender.track is often null. Identify the local video m-line only:
  // send-capable, not screenshare, not audio. Recvonly remote video is excluded.
  // Multiple matches fail closed instead of replacing the wrong sender.
  const candidates = (pc.getTransceivers?.() ?? []).filter((transceiver) => {
    if (!transceiver?.sender || transceiver.sender === screenshareSender) return false;
    if (!transceiverCanSend(transceiver)) return false;
    const senderKind = transceiver.sender.track?.kind;
    if (senderKind === 'audio') return false;
    if (senderKind === 'video') return true;
    return transceiver.receiver?.track?.kind === 'video';
  });

  return candidates.length === 1 ? candidates[0].sender : null;
};

const assertCallActive = (call) => {
  if (isCallEnded(call)) {
    throw createAbortError('Call ended during camera restore');
  }
};

const isCameraOpInterrupted = (call, signal) =>
  Boolean(signal?.aborted) || isCallEnded(call);

const throwIfCameraOpInterrupted = (call, signal) => {
  if (signal?.aborted) {
    throw createAbortError('Camera restore aborted');
  }
  assertCallActive(call);
};

const detachTrackFromCallStreams = (call, videoTrack) => {
  if (!call || !videoTrack) return;
  collectCallLocalMediaStreams(call).forEach((stream) => {
    try {
      stream.removeTrack?.(videoTrack);
    } catch (_) {}
  });
};

const discardRestoredVideoTrack = async (call, videoTrack) => {
  if (!videoTrack) return;

  // Clear the sender while the track is still identifiable. Stopping first
  // can leave the peer attached to an ended track until the next restore.
  const sender = getCallVideoSender(call);
  if (sender?.track === videoTrack && typeof sender.replaceTrack === 'function') {
    try {
      await withTimeout(
        sender.replaceTrack(null),
        SDK_REPLACE_TRACK_TIMEOUT_MS,
        'replaceTrack(null) timeout'
      );
    } catch (_) {}
  }

  detachTrackFromCallStreams(call, videoTrack);
  releaseUnadoptedCallVideoTrack(videoTrack);
};

const unmuteCallVideoAfterRestore = async (call, signal) => {
  if (typeof call.setLocalVideoMuted !== 'function') return;

  const attemptUnmute = () =>
    withTimeout(
      call.setLocalVideoMuted(false),
      SDK_MEDIA_CONTROL_TIMEOUT_MS,
      'setLocalVideoMuted timeout',
      signal
    );

  // replaceTrack may already be sending; only fail if nothing is live.
  const acceptIfVideoLive = (error) => {
    if (!callHasLiveVideoTrack(call)) throw error;
  };

  try {
    await attemptUnmute();
  } catch (error) {
    if (isAbortError(error) || isCameraOpInterrupted(call, signal)) throw error;
    captureChatException(error, { tags: { feature: 'call', action: 'restore-camera-unmute' } });

    // Timeout leaves setLocalVideoMuted running. A second call races the first
    // (background getUserMedia + SDK mute state). Do not retry.
    if (isTimeoutError(error)) {
      acceptIfVideoLive(error);
      return;
    }

    try {
      await attemptUnmute();
    } catch (retryError) {
      if (isAbortError(retryError) || isCameraOpInterrupted(call, signal)) throw retryError;
      captureChatException(retryError, {
        tags: { feature: 'call', action: 'restore-camera-unmute-retry' },
      });
      acceptIfVideoLive(retryError);
    }
  }
};

/**
 * Re-acquire camera (video-only) and put it back on the call, then unmute via
 * the public Matrix API. setLocalVideoMuted(false) skips getUserMedia when a
 * video track is already on localUsermediaStream, which avoids the iOS
 * audio+video capture failure.
 *
 * Also used for front/back flip so camera on/off and flip share one path
 * instead of MediaHandler.setVideoInput (which stops the mic and was being
 * forced back to the front camera).
 */
const replaceLocalCallVideoTrack = async (call, { signal, exactDeviceId, facingMode } = {}) => {
  throwIfCameraOpInterrupted(call, signal);

  const acquireTimeout = createTimeoutController(SDK_CAMERA_ON_TIMEOUT_MS);
  const onExternalAbort = () => acquireTimeout.abort();
  if (signal) {
    signal.addEventListener('abort', onExternalAbort, { once: true });
  }

  /** @type {MediaStreamTrack | null} */
  let videoTrack = null;
  try {
    try {
      videoTrack = await acquireCallVideoTrack({
        exactDeviceId:
          exactDeviceId || getCurrentVideoDeviceId(call.localUsermediaStream) || undefined,
        facingMode:
          facingMode || getVideoFacingModeFromStream(call.localUsermediaStream) || undefined,
        signal: acquireTimeout.signal,
      });
    } catch (error) {
      if (isAbortError(error) && acquireTimeout.timedOut && !signal?.aborted) {
        throw createTimeoutError('Camera re-acquire timed out');
      }
      throw error;
    }
    acquireTimeout.dispose();

    throwIfCameraOpInterrupted(call, signal);
    videoTrack.enabled = true;

    const localStream = call.localUsermediaStream;
    if (!localStream) {
      throw new Error('No local usermedia stream available to restore camera');
    }
    if (!replaceVideoTrackOnStream(localStream, videoTrack)) {
      throw new Error('Failed to attach camera track to local stream');
    }

    const sender = getCallVideoSender(call);
    if (!sender?.replaceTrack) {
      throw new Error('No video sender available to restore camera');
    }
    try {
      await withTimeout(
        sender.replaceTrack(videoTrack),
        SDK_REPLACE_TRACK_TIMEOUT_MS,
        'replaceTrack timeout',
        signal
      );
    } catch (error) {
      // Timeout is success only when the sender already adopted this track.
      // Otherwise fail closed so discard can stop the camera instead of
      // reporting ON while the peer still has no video.
      if (
        isTimeoutError(error) &&
        !isCameraOpInterrupted(call, signal) &&
        sender.track === videoTrack
      ) {
        captureChatException(error, { tags: { feature: 'call', action: 'replace-track-timeout' } });
      } else {
        throw error;
      }
    }

    collectCallLocalMediaStreams(call).forEach((stream) => {
      if (stream === localStream) return;
      replaceVideoTrackOnStream(stream, videoTrack);
    });

    throwIfCameraOpInterrupted(call, signal);
    await unmuteCallVideoAfterRestore(call, signal);
    throwIfCameraOpInterrupted(call, signal);

    // Commit only after unmute succeeds (or is skipped because RTP is already live).
    // Failure/abort before this keeps the track uncommitted so discard can stop it.
    adoptLiveVideoTrackIntoCallCaptureCache(videoTrack);
    syncMediaHandlerVideoInput(
      call.client,
      videoTrack.getSettings?.()?.deviceId ||
        getCurrentVideoDeviceId(localStream) ||
        exactDeviceId
    );
    videoTrack = null;
  } catch (error) {
    await discardRestoredVideoTrack(call, videoTrack);
    throw error;
  } finally {
    signal?.removeEventListener('abort', onExternalAbort);
    acquireTimeout.dispose();
  }
};

export const toggleCallMicrophone = async ({ activeCall, micMuted, setMicMuted }) => {
  if (isCallEnded(activeCall)) return;
  const next = !micMuted;
  // Keep UI responsive on mobile while SDK/device operations settle.
  setMicMuted(next);
  try {
    if (typeof activeCall.setMicrophoneMuted === 'function') {
      await withTimeout(
        activeCall.setMicrophoneMuted(next),
        SDK_MEDIA_CONTROL_TIMEOUT_MS,
        'setMicrophoneMuted timeout'
      );
    } else {
      setLocalOutboundAudioTracksEnabled(activeCall, !next);
    }
  } catch (error) {
    captureChatException(error, { tags: { feature: 'call', action: 'toggle-mic' } });
  } finally {
    // Ensure real outbound tracks match intended mute state even if SDK metadata
    // propagation is delayed or a device swap recreated audio tracks.
    setLocalOutboundAudioTracksEnabled(activeCall, !next);
  }
};

/**
 * Toggle subscriber outbound video during a Matrix 1:1 call.
 * Returns the actual local camera-off state so the UI can sync to reality
 * instead of guessing whether to revert.
 *
 * @param {{
 *   activeCall: object,
 *   cameraOff: boolean,
 *   setCameraOff: function,
 *   signal?: AbortSignal,
 * }} params
 * @param {boolean} params.cameraOff - Current state: true when the camera is off.
 * @returns {Promise<{ ok: boolean, cameraOff: boolean, aborted?: boolean }>}
 */
export const toggleCallCamera = async ({ activeCall, cameraOff, setCameraOff, signal }) => {
  if (isCameraOpInterrupted(activeCall, signal)) {
    return { ok: false, cameraOff, aborted: true };
  }

  const nextCameraOff = !cameraOff;
  setCameraOff(nextCameraOff);

  // After the SDK's 120ms mute timer, video tracks are stopped. Unmute then calls
  // getUserMedia(audio+video), which fails on iOS because the mic is still held.
  // Re-acquire camera only and replace the video sender instead.
  if (!nextCameraOff && !callHasLiveVideoTrack(activeCall)) {
    try {
      await replaceLocalCallVideoTrack(activeCall, { signal });
      if (isCameraOpInterrupted(activeCall, signal)) {
        const actualCameraOff = getLocalCameraOffState(activeCall);
        return { ok: false, cameraOff: actualCameraOff, aborted: true };
      }
      setLocalOutboundVideoTracksEnabled(activeCall, true);
      setCameraOff(false);
      return { ok: true, cameraOff: false };
    } catch (error) {
      const aborted = isAbortError(error) || isCameraOpInterrupted(activeCall, signal);
      if (!aborted) {
        captureChatException(error, { tags: { feature: 'call', action: 'restore-camera' } });
        setCameraOff(true);
        setLocalOutboundVideoTracksEnabled(activeCall, false);
        return { ok: false, cameraOff: true };
      }
      return { ok: false, cameraOff: getLocalCameraOffState(activeCall), aborted: true };
    }
  }

  // Camera OFF is fast (track disable + 120ms SDK stop-timer), so a short timeout is fine.
  // Camera ON with tracks still live (unmute within 120ms) only re-enables them.
  const timeoutMs = nextCameraOff ? SDK_MEDIA_CONTROL_TIMEOUT_MS : SDK_CAMERA_ON_TIMEOUT_MS;

  let sdkCallSucceeded = false;
  try {
    if (typeof activeCall.setLocalVideoMuted === 'function') {
      await withTimeout(
        activeCall.setLocalVideoMuted(nextCameraOff),
        timeoutMs,
        'setLocalVideoMuted timeout',
        signal
      );
      sdkCallSucceeded = true;
    } else {
      setLocalOutboundVideoTracksEnabled(activeCall, !nextCameraOff);
      sdkCallSucceeded = true;
    }
  } catch (error) {
    const aborted = isAbortError(error) || isCameraOpInterrupted(activeCall, signal);
    if (!aborted) {
      captureChatException(error, { tags: { feature: 'call', action: 'toggle-camera' } });
    }
    if (aborted) {
      return { ok: false, cameraOff: getLocalCameraOffState(activeCall), aborted: true };
    }
  } finally {
    // Ensure real outbound tracks match the intended state even if SDK metadata propagation
    // is delayed or a device swap recreated video tracks mid-operation.
    if (!isCameraOpInterrupted(activeCall, signal)) {
      setLocalOutboundVideoTracksEnabled(activeCall, !nextCameraOff);
    }
  }

  if (isCameraOpInterrupted(activeCall, signal)) {
    return { ok: false, cameraOff: getLocalCameraOffState(activeCall), aborted: true };
  }

  const actualCameraOff = getLocalCameraOffState(activeCall);
  setCameraOff(actualCameraOff);
  return { ok: sdkCallSucceeded && actualCameraOff === nextCameraOff, cameraOff: actualCameraOff };
};

/** Narrow / mobile UA — used to offer in-call camera flip where multi-camera phones are common. */
export const shouldOfferCallCameraFlip = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const mobileUa =
    /Android.+Mobile|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const narrow = window.matchMedia?.('(max-width: 768px)')?.matches ?? false;
  return mobileUa || narrow;
};

/**
 * Switch to the opposite built-in camera during a Matrix 1:1 call (front ↔ back).
 * Uses the same video-only replaceTrack path as camera on/off so iOS does not
 * drop the mic, and so the capture cache cannot keep serving the selfie camera.
 */
export const flipCallCamera = async ({ client, activeCall, localStream }) => {
  if (!client || isCallEnded(activeCall)) {
    return { ok: false, reason: 'no-call' };
  }

  const enumerated = await enumerateVideoInputDevices();
  if (!enumerated.ok) return { ok: false, reason: enumerated.reason };
  const { videoInputs } = enumerated;
  if (videoInputs.length < 2) {
    return { ok: false, reason: 'single-camera' };
  }

  const currentStream = activeCall?.localUsermediaStream || localStream;
  const previousDeviceId = getCurrentVideoDeviceId(currentStream);
  const previousFacing = getVideoFacingModeFromStream(currentStream);
  const target = pickFlipCameraTarget(videoInputs, previousDeviceId, previousFacing);
  if (!target?.deviceId) {
    return { ok: false, reason: 'no-target' };
  }

  try {
    syncMediaHandlerVideoInput(client, target.deviceId);
    await replaceLocalCallVideoTrack(activeCall, {
      exactDeviceId: target.deviceId,
      facingMode: target.facingMode,
    });
    const appliedFacing =
      getVideoFacingModeFromStream(activeCall?.localUsermediaStream || localStream) ||
      target.facingMode ||
      null;
    return { ok: true, facingMode: appliedFacing };
  } catch (error) {
    captureChatException(error, { tags: { feature: 'call', action: 'flip-camera' } });
    try {
      syncMediaHandlerVideoInput(client, previousDeviceId);
      await replaceLocalCallVideoTrack(activeCall, {
        exactDeviceId: previousDeviceId || undefined,
        facingMode: previousFacing || undefined,
      });
    } catch (restoreError) {
      captureChatException(restoreError, {
        tags: { feature: 'call', action: 'flip-camera-restore' },
      });
    }
    return { ok: false, reason: 'set-video' };
  }
};

/**
 * Best-effort reset to front/selfie camera for new calls.
 * This keeps "flip off" aligned with the user-facing camera by default.
 */
export const ensureFrontCallCamera = async ({ client, activeCall, localStream }) => {
  if (!client || isCallEnded(activeCall)) {
    return { ok: false, reason: 'no-call' };
  }

  const enumerated = await enumerateVideoInputDevices();
  if (!enumerated.ok) return { ok: false, reason: enumerated.reason };
  const { videoInputs } = enumerated;
  if (!videoInputs.length) {
    return { ok: false, reason: 'no-camera' };
  }

  const currentStream = activeCall?.localUsermediaStream || localStream;
  const currentId = getCurrentVideoDeviceId(currentStream);
  const currentFacing = getVideoFacingModeFromStream(currentStream);
  const frontDevice = pickCallCameraForFacing(videoInputs, 'user');

  if (!frontDevice?.deviceId) {
    return { ok: false, reason: 'no-front-target' };
  }
  if (currentId === frontDevice.deviceId || currentFacing === 'user') {
    syncMediaHandlerVideoInput(client, currentId || frontDevice.deviceId);
    return { ok: true, changed: false };
  }

  try {
    await replaceLocalCallVideoTrack(activeCall, {
      exactDeviceId: frontDevice.deviceId,
      facingMode: 'user',
    });
    syncMediaHandlerVideoInput(
      client,
      getCurrentVideoDeviceId(activeCall.localUsermediaStream) || frontDevice.deviceId
    );
    return { ok: true, changed: true };
  } catch (error) {
    captureChatException(error, { tags: { feature: 'call', action: 'ensure-front-camera' } });
    return { ok: false, reason: 'set-video' };
  }
};
